import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import axios from 'axios';

const EventForm = () => {
  const { t } = useTranslation();
  const { user, getUserGroups } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    description: ''
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [eventOwner, setEventOwner] = useState(null);
  
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchUserGroups();
    if (id) {
      setIsEditing(true);
      fetchEvent();
    }
  }, [id]);

  useEffect(() => {
    // Load users when group is selected (for new events)
    if (!isEditing && selectedGroupId) {
      fetchAvailableUsers();
    }
  }, [selectedGroupId, isEditing]);

  const fetchUserGroups = async () => {
    try {
      const result = await getUserGroups();
      if (result.success) {
        setUserGroups(result.groups);
        // Auto-select first group for new events
        if (!isEditing && result.groups.length > 0) {
          setSelectedGroupId(result.groups[0].id.toString());
        }
      }
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
    }
  };

  const fetchAvailableUsers = async (eventGroupCode = null) => {
    try {
      if (eventGroupCode) {
        // For editing: Get users from the event's specific group
        const eventGroup = userGroups.find(g => g.code === eventGroupCode);
        if (eventGroup) {
          console.log('Loading users for group:', eventGroup);
          const response = await axios.get(`/api/users/by-group/${eventGroup.id}`);
          console.log('Users loaded:', response.data);
          setAvailableUsers(response.data);
          setSelectedGroupId(eventGroup.id.toString());
        } else {
          console.error('Event group not found in user groups');
          setAvailableUsers([]);
        }
      } else {
        // For new events: Get users from selected group
        if (selectedGroupId) {
          console.log('Loading users for selected group:', selectedGroupId);
          const response = await axios.get(`/api/users/by-group/${selectedGroupId}`);
          console.log('Users loaded for selected group:', response.data);
          setAvailableUsers(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch available users:', error);
      setAvailableUsers([]);
    }
  };

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`/api/events/${id}`);
      const event = response.data;
      setEventOwner(event.createdBy);
      
      // Check if user owns the event
      if (event.createdBy !== user.id) {
        setError(t('events.noPermissionEdit') || 'You do not have permission to edit this event');
        return;
      }

      // Note: Participants are managed via join/leave buttons on the event list
      
      setFormData({
        title: event.title,
        date: event.date,
        time: event.time,
        description: event.description || ''
      });

      // Load available users for this event's group
      fetchAvailableUsers(event.groupCode);
    } catch (error) {
      setError(error.response?.data?.error || t('events.failedToFetch') || 'Failed to fetch event');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Check authorization for editing
    if (isEditing && eventOwner && eventOwner !== user.id) {
      setError(t('events.noPermissionEdit') || 'You do not have permission to edit this event');
      setIsLoading(false);
      return;
    }

    let eventData = {
      title: formData.title,
      date: formData.date,
      time: formData.time,
      description: formData.description
    };

    // For new events, include selected group
    if (!isEditing) {
      if (!selectedGroupId) {
        setError(t('events.selectGroupRequired') || 'Please select a group for this event');
        setIsLoading(false);
        return;
      }
      eventData.groupId = selectedGroupId;
    }

    // Note: Participants are not managed during editing
    // Users can join/leave events directly from the event list

    try {
      if (isEditing) {
        await axios.put(`/api/events/${id}`, eventData);
      } else {
        await axios.post('/api/events', eventData);
      }
      navigate('/');
    } catch (error) {
      setError(error.response?.data?.error || t('events.failedToSave') || 'Failed to save event');
      setIsLoading(false);
    }
  };

  const hasPermissionError = isEditing && eventOwner && eventOwner !== user.id;

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>{isEditing ? (t('events.editEventTitle') || 'Edit Event') : (t('events.createEventTitle') || 'Create New Event')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">{t('events.eventTitle') || 'Event Title'}:</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              disabled={hasPermissionError}
              required
            />
          </div>

          {!isEditing && (
            <div className="form-group">
              <label htmlFor="group">{t('groups.group') || 'Group'}:</label>
              <select
                id="group"
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                required
                className="form-select"
              >
                <option value="">{t('groups.selectGroup') || 'Select a group...'}</option>
                {userGroups.map(group => (
                  <option key={group.id} value={group.id.toString()}>
                    {group.title} ({group.memberCount} {t('groups.members') || 'members'})
                  </option>
                ))}
              </select>
              <small>{t('events.chooseGroup') || 'Choose which group this event belongs to'}</small>
            </div>
          )}
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">{t('events.date') || 'Date'}:</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                disabled={hasPermissionError}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="time">{t('events.time') || 'Time'}:</label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                disabled={hasPermissionError}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="description">{t('events.description') || 'Description'} ({t('common.optional') || 'Optional'}):</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={hasPermissionError}
              rows="4"
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-actions">
            <button type="submit" disabled={isLoading || hasPermissionError} className="btn btn-primary">
              {isLoading ? (t('common.saving') || 'Saving...') : (isEditing ? (t('events.updateEvent') || 'Update Event') : (t('events.createEvent') || 'Create Event'))}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="btn btn-secondary"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm; 